// V2-TB embed config + token acquisition. Third instance of the V2-GAC embed pattern (see utils/v2gac.ts,
// which documents the security model in detail; utils/v2events.ts is the second).
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST tb/v2/token`, added to the
// existing TBController) — the server derives playerId AND the GOH guildId from the authenticated session and
// stamps aud="tb", which the TB BFF requires. The signing secret never reaches the browser.
//
// DEV FALLBACK (opt-in only): signDevTBToken() mints the HS256 token IN THE BROWSER with a shared secret —
// forgeable, never in production. Enable explicitly with REACT_APP_V2TB_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-TB app's origin, per environment. Override with REACT_APP_V2TB_URL.
export function getV2TBUrl(): string {
  const override = process.env.REACT_APP_V2TB_URL;
  if (override) return override;

  const host = window.location.host;
  // ⚠️ PROD + STAGING point at the container app's own FQDN, not <app>.hotutils.com. Those subdomains
  // were an aspirational convention copied from the first GAC embed and were NEVER provisioned - all twelve
  // failed to resolve (verified 2026-08-12). There is no staging container app either, so staging uses the
  // PROD one, which already accepts staging.hotutils.com as a framer via its PARENT_ORIGIN allowlist.
  // 🔴 The tab is still gated OFF outside development by isV2TBEnabled() - fixing the URL first means the
  // wiring is correct with nothing user-visible to roll back.
  if (host === 'hotutils.com' || host === 'www.hotutils.com') return 'https://hotutils-tb-prod.lemonsea-9b825e1d.southcentralus.azurecontainerapps.io';
  if (host.includes('localhost')) return 'http://localhost:5175'; // local TB dev server (GAC 5173, Events 5174)
  // Deployed development site -> the deployed dev TB container app.
  if (host.startsWith('development')) return 'https://hotutils-tb-dev.whitemushroom-165fe03a.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-tb-prod.lemonsea-9b825e1d.southcentralus.azurecontainerapps.io';
}

export function getV2TBOrigin(): string {
  return new URL(getV2TBUrl()).origin;
}

/**
 * The V2-TB tab is DEV-ONLY for now — only surfaced (tab + route) on the Development site and in local dev;
 * suppressed on staging/production until ready. Same gating rule as V2-GAC and V2-Events.
 */
export function isV2TBEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function useTBDevSigner(): boolean {
  return process.env.REACT_APP_V2TB_DEV_SIGN === 'true';
}

interface TBV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token (`POST tb/v2/token` on TBController). The server
 * derives playerId + the GOH guildId from the session and signs with aud="tb"; the browser only receives the
 * finished JWT.
 */
export async function fetchTBToken(sessionId?: string): Promise<string> {
  const resp: TBV2TokenResponse = await BaseAPI.postToApi('tb/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('TB token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2TBTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
  guildId?: string;
}

// The shared HS256 secret the TB BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the TB app's
// .env.example. Override with REACT_APP_V2TB_DEV_SECRET. Dev fallback signer only.
function getDevSecret(): string {
  return process.env.REACT_APP_V2TB_DEV_SECRET || 'dev-shared-secret-change-me';
}

// The GOH guild id (string) for the DEV SIGNER ONLY. The website client only has the legacy numeric guild id,
// so a dev-signed token's guild scope must come from env config — deliberately NO default: the production
// path (HotService) derives the real guild server-side, so this matters only when REACT_APP_V2TB_DEV_SIGN=true.
function getDevGuildId(): string | undefined {
  return process.env.REACT_APP_V2TB_DEV_GUILD_ID || undefined;
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the TB BFF will verify. aud MUST be "tb" — the TB BFF is configured
 * with PARENT_TOKEN_AUDIENCE=tb, so a GAC or Events token can never authorize the TB app and vice versa.
 */
export async function signDevTBToken(claims: V2TBTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const devGuildId = getDevGuildId();
  const payload = {
    ...(devGuildId ? { guildId: devGuildId } : {}),
    ...claims,
    iss: 'hotutils',
    aud: 'tb',
    iat: now,
    exp: now + 60 * 60, // 1h
  };
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
